import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex, Loader } from '@chakra-ui/react';
import { FaEdit, FaPlus } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useColorModeValue } from '../../components/ui/color-mode';
import { darkMode, lightMode } from '../../theme/config';
import { loadModelsRequest } from '../../store/modelsSlice';
import { openDialog, closeDialog } from '../../store/dialogSlice';
import TableContainer from '../../components/shared/table/TableContainer';
import PaginationComponent from '../../components/shared/panel/Pagination';
import CatalogueDialog from './CatalogueDialog';
import ButtonBox from '../../components/shared/buttons/ButtonBox';

const catalogueColumns = [
  { key: 'model', label: 'Model', width: '20%', textAlign: 'center', maxTextLength: 15 },
  { key: 'name', label: 'Name', width: '20%', textAlign: 'center', maxTextLength: 15 },
  { key: 'category', label: 'Category', width: '20%', textAlign: 'center', maxTextLength: 12 },
  { key: 'subCategory', label: 'Sub Category', width: '20%', textAlign: 'center', maxTextLength: 12 },
  { key: 'description', label: 'Description', width: '30%', textAlign: 'center', maxTextLength: 15 },
  { key: 'images', label: 'Images', width: '10%', textAlign: 'center', maxTextLength: 20, allowCopy: false },
  { key: 'warrantyPeriodInDays', label: 'Warranty(Days)', width: '3%', textAlign: 'center', showTooltip: false, showInChip: true },
  { key: 'extWarrantyPeriodInDays', label: 'Extended Warranty(Days)', width: '3%', textAlign: 'center', showTooltip: false, showInChip: true },
  { key: 'actions', label: 'Actions', width: '1%', textAlign: 'center' },
];

const CateloguesTab = ({ activeTab }) => {
  const dispatch = useDispatch();
  const borderColor = useColorModeValue(lightMode.containerBorderColor, darkMode.containerBorderColor);
  const backgroundColor = useColorModeValue(lightMode.boxBackgroundColor, darkMode.boxBackgroundColor);

  const [currentPage, setCurrentPage] = useState(1);
  // track pagination tokens for previous pages
  const [tokenStack, setTokenStack] = useState([null]);
  // track page tokens for direct navigation
  const [pageTokens, setPageTokens] = useState({ 1: null });
  const [selectedCatalogue, setSelectedCatalogue] = useState(null);
  const pageSize = 10;

  const { items, loading, nextToken, count } = useSelector((state) => state.models || {
    items: [], loading: false, nextToken: null, count: 0
  });

  const { productCategories, productSubcategories } = useSelector((state) => state.categories || {
    productCategories: [],
    productSubcategories: {}
  });

  const addDialogOpen = useSelector((state) => state.dialogs.addCatalogue);
  const editDialogOpen = useSelector((state) => state.dialogs.editCatalogue);

  useEffect(() => {
    if (activeTab === 'catalogues') {
      dispatch(loadModelsRequest({ limit: pageSize, nextToken: null }));
      setTokenStack([null]);
      setPageTokens({ 1: null });
      setCurrentPage(1);
    }
  }, [activeTab, dispatch]);

  const formatCataloguesData = useCallback((items = []) => {
    return (items || []).map((item, idx) => {
      // Only resolve human-readable labels from categories/subcategories.
      // Do NOT fall back to raw id values; show '-' when label not available.
      const resolvedCategory = productCategories.find(cat => cat.value === item.category);
      const categoryLabel = resolvedCategory ? resolvedCategory.label : null;

      const subcategoryMap = productSubcategories || {};
      const resolvedSub = (subcategoryMap[item.category] || []).find(sub => sub.value === item.subCategory);
      const subCategoryLabel = resolvedSub ? resolvedSub.label : null;

      return {
        id: item.id, // Keep id for reference
        name: item.name || '-',
        category: categoryLabel ? categoryLabel : '-',
        categoryObject: categoryLabel ? { value: item.category, label: categoryLabel } : undefined,
        subCategory: subCategoryLabel ? subCategoryLabel : '-',
        subCategoryObject: subCategoryLabel ? { value: item.subCategory, label: subCategoryLabel } : undefined,
        model: item.model || '-',
        description: item.description || '-',
        images: item.images && item.images.length > 0
          ? (Array.isArray(item.images) ? item.images.join(', ') : item.images)
          : '-',
        warrantyPeriodInDays: item.warrantyPeriodInDays || '-',
        extWarrantyPeriodInDays: item.extWarrantyPeriodInDays || '-',
      };
    });
  }, [productCategories, productSubcategories]);

  const handleEdit = (row) => {
    // Find the original item from items array
    const originalItem = items.find(item => item.id === row.id || item.name === row.name);
    const formattedRow = formattedData.find(r => r.id === row.id || r.name === row.name) || {};
    const merged = { ...(originalItem || row), categoryObject: formattedRow.categoryObject, subCategoryObject: formattedRow.subCategoryObject };
    setSelectedCatalogue(merged);
    dispatch(openDialog('editCatalogue'));
  };

  const handleAddCatalogue = () => {
    setSelectedCatalogue(null);
    dispatch(openDialog('addCatalogue'));
  };

  const handleCloseAddDialog = () => {
    dispatch(closeDialog('addCatalogue'));
    setSelectedCatalogue(null);
  };

  const handleCloseEditDialog = () => {
    dispatch(closeDialog('editCatalogue'));
    setSelectedCatalogue(null);
  };

  const actions = [
    { name: 'Edit', icon: <FaEdit />, function: handleEdit },
    // { name: 'Delete', icon: <FaTrash />, function: handleDelete },
  ];

  const formattedData = useMemo(() => formatCataloguesData(items), [items, formatCataloguesData]);
  // For token-based pagination, we don't know the exact total pages until we reach the end
  // So we calculate based on what we know and add one more if there's a nextToken
  const estimatedTotalPages = count ? Math.ceil(count / pageSize) : (nextToken ? currentPage + 1 : currentPage);
  const totalPages = Math.max(estimatedTotalPages, currentPage);
  const paginatedData = formattedData;

  // Get available pages (pages we have tokens for or can navigate to)
  const availablePages = Object.keys(pageTokens).map(Number).sort((a, b) => a - b);
  if (nextToken && !availablePages.includes(currentPage + 1)) {
    availablePages.push(currentPage + 1);
  }

  const handlePageChange = (page) => {
    // For token-based pagination, we can only navigate to pages we've already visited
    // or sequential navigation to the next page
    if (pageTokens[page] !== undefined) {
      // We have the token for this page, navigate directly
      const token = pageTokens[page];
      dispatch(loadModelsRequest({ limit: pageSize, nextToken: token }));
      setCurrentPage(page);

      // Update token stack to reflect current position
      const newStack = [];
      for (let i = 1; i <= page; i++) {
        if (pageTokens[i] !== undefined) {
          newStack.push(pageTokens[i]);
        }
      }
      setTokenStack(newStack);
    } else if (page === currentPage + 1 && nextToken) {
      // Sequential navigation to next page
      handleNext();
    } else {
      // Can't jump to this page directly with token-based pagination
      console.warn(`Cannot navigate to page ${page} - token not available`);
    }
  };

  const handlePrev = () => {
    if (tokenStack.length > 1) {
      // pop current token
      const newStack = [...tokenStack]; newStack.pop();
      const prevToken = newStack[newStack.length - 1];
      setTokenStack(newStack);
      dispatch(loadModelsRequest({ limit: pageSize, nextToken: prevToken }));
      setCurrentPage((p) => p - 1);
    }
  };

  const handleNext = () => {
    if (nextToken) {
      const newPage = currentPage + 1;
      // Store the token for the new page
      setPageTokens(prev => ({ ...prev, [newPage]: nextToken }));
      // push current token then fetch next
      setTokenStack((stack) => [...stack, nextToken]);
      dispatch(loadModelsRequest({ limit: pageSize, nextToken }));
      setCurrentPage(newPage);
    }
  };

  return (
    <Box p={4}>
      {activeTab === 'catalogues' && (
        <Flex justify="flex-end" mb={4}>
          <ButtonBox
            name="Add Catalogue"
            leftIcon={<FaPlus />}
            colorPalette="blue"
            variant="solid"
            onClick={handleAddCatalogue}
            size="sm"
          />
        </Flex>
      )}

      <Box
        borderRadius="sm"
        border="1px solid"
        borderColor={borderColor}
        bg={backgroundColor}
        p={4}
      >
        {loading && (
          <Box textAlign="center" py={4}>
            <Loader />Loading catalogues...
          </Box>
        )}

        {!loading && formattedData.length === 0 && (
          <Box textAlign="center" py={4}>
            No catalogues found
          </Box>
        )}

        {!loading && formattedData.length > 0 && (
          <>
            <TableContainer
              showColumnBorder={true}
              columns={catalogueColumns}
              rows={paginatedData}
              actions={actions}
              truncateText={true}
              maxTextLength={50}
              showTooltip={true}
            />

            <Flex justify="center" mt={4}>
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onPrev={handlePrev}
                onNext={handleNext}
                availablePages={availablePages}
                hasNextToken={!!nextToken}
                size="sm"
                colorScheme="blue"
              />
            </Flex>
          </>
        )}
      </Box>

      {/* Add Catalogue Dialog */}
      <CatalogueDialog
        isOpen={addDialogOpen}
        onClose={handleCloseAddDialog}
        mode="add"
      />

      {/* Edit Catalogue Dialog */}
      <CatalogueDialog
        isOpen={editDialogOpen}
        onClose={handleCloseEditDialog}
        mode="edit"
        initialData={selectedCatalogue}
      />
    </Box>
  );
};

export default CateloguesTab;
